import PersonAdd from '@mui/icons-material/PersonAddOutlined'
import UploadFile from '@mui/icons-material/UploadFileOutlined'
import {
  Alert,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'preact/hooks'
import type { Position } from '../api/generated/Position'
import type { Volunteer } from '../api/generated/Volunteer'
import { useApi } from '../api/useApi'
import { volunteerName } from '../assignments/status'
import { Applications } from '../events/Applications'
import { useEvent } from '../events/EventContext'
import { RegistrationLink } from '../events/RegistrationLink'
import { SectionPage } from '../events/SectionPage'
import { useOrganizerData } from '../organizer/OrganizerData'
import { volunteersToAssign } from '../organizer/overview'
import { QuestionList } from '../questions/QuestionList'
import { AddVolunteerDialog } from '../volunteers/AddVolunteerDialog'
import { ImportDialog } from '../volunteers/ImportDialog'
import { VolunteerCard } from '../volunteers/VolunteerCard'

function wishes(volunteer: Volunteer, positions: Position[]): string {
  if (volunteer.availability.length === 0) return 'Disponibilités non indiquées'
  const names = positions.filter((p) => volunteer.position_ids.includes(p.id)).map((p) => p.name)
  return names.length > 0 ? names.join(', ') : 'Aucun poste souhaité'
}

/** Volunteers accepted on no shift, each opening their card to assign them. */
function VolunteersToAssign({ slug }: { slug: string }) {
  const { volunteers } = useOrganizerData()
  const positions = useApi<Position[]>(`/events/${slug}/positions`)
  const [shown, setShown] = useState<Volunteer>()
  const toAssign = volunteers.data && volunteersToAssign(volunteers.data)
  const error = volunteers.error ?? positions.error
  return (
    <Stack spacing={1.5}>
      <Typography variant="h6" component="h2">
        Bénévoles à répartir
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {toAssign?.length === 0 && (
        <Typography color="text.secondary">Tous les bénévoles ont au moins un créneau.</Typography>
      )}
      {toAssign && toAssign.length > 0 && (
        <Paper variant="outlined">
          <List dense disablePadding>
            {toAssign.map((volunteer) => (
              <ListItemButton key={volunteer.user_id} onClick={() => setShown(volunteer)}>
                <ListItemText
                  primary={volunteerName(volunteer)}
                  secondary={wishes(volunteer, positions.data ?? [])}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
      {shown && <VolunteerCard slug={slug} volunteer={shown} onClose={() => setShown(undefined)} />}
    </Stack>
  )
}

export function VolunteersPage() {
  const { event } = useEvent()
  const { shifts, volunteers } = useOrganizerData()
  const [dialog, setDialog] = useState<'add' | 'import'>()
  const close = () => {
    setDialog(undefined)
    shifts.reload()
    volunteers.reload()
  }
  return (
    <SectionPage
      title="Bénévoles"
      actions={
        <>
          <Button variant="outlined" startIcon={<UploadFile />} onClick={() => setDialog('import')}>
            Importer des bénévoles
          </Button>
          <Button variant="contained" startIcon={<PersonAdd />} onClick={() => setDialog('add')}>
            Ajouter un bénévole
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Inscriptions
          </Typography>
          <Applications />
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Lien d'inscription
          </Typography>
          <RegistrationLink event={event} />
        </Paper>
        <VolunteersToAssign slug={event.slug} />
        <Paper variant="outlined" sx={{ p: 2 }}>
          <QuestionList slug={event.slug} />
        </Paper>
      </Stack>
      {dialog === 'add' && <AddVolunteerDialog slug={event.slug} onClose={close} />}
      {dialog === 'import' && <ImportDialog slug={event.slug} onClose={close} />}
    </SectionPage>
  )
}
